import {FullDate as ApiFullDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import styled from 'styled-components';
import { $enum } from 'ts-enum-util';

import { TableSortable } from '@/components/table/table-sortable';
import { TableSortableColumn } from '@/components/table/table-sortable-types';
import {
    formatApiDateShortUniversal, formatApiTimeUniversal,
} from '@/util/apidate-util';
import {getSignalTypeDualLabel, getSignalTypeLabel, getSignalTypeUnit} from '@/util/signal-type-util';

export type BiometricHistorySpotData = {
    fullDate: ApiFullDate;
    seconds: number;
    values: Array<{
        signalType: SignalType;
        quality: number;
        value: number;
    }>;
}

type HealthReportSpotSignalsTableProps = {
    data: BiometricHistorySpotData[];
};
//----------------------------------------------------------------------------------------------------------------------

function tableSortableHeaderColumn(signalType: SignalType) {
    return {
        id: $enum(SignalType).getKeyOrDefault(signalType, ''),
        label: getSignalTypeLabel(signalType),
        className: 'signalTableHeader',
        sortable: false,
    };
}
function tableSortableHeaderColumnDual(signalType: SignalType) {
    return {
        id: $enum(SignalType).getKeyOrDefault(signalType, ''),
        label: getSignalTypeDualLabel(signalType),
        className: 'signalTableHeader',
        sortable: false,
    };
}

function HealthReportSpotSignalsTable({data}: HealthReportSpotSignalsTableProps) {
    // Can't be set outside the function, because of the translations
    const tableSortableHeaderColumns: TableSortableColumn<BiometricHistorySpotData>[] = [
        {
            id: 'time',
            label: 'Time',
            sortable: true,
            compareFn: (a, b) => {
                return a.seconds - b.seconds;
            },
        },
        tableSortableHeaderColumn(SignalType.Signal_PR),
        tableSortableHeaderColumn(SignalType.Signal_RR),
        tableSortableHeaderColumn(SignalType.Signal_SPO2),
        tableSortableHeaderColumnDual(SignalType.Signal_BP_SYS),
        tableSortableHeaderColumn(SignalType.NEWS2),
    ];
    return (
        <TableSortable
            data={data}
            keyFn={item => `${item.seconds}`}
            columns={tableSortableHeaderColumns}
            defaultSorting={{id: 'timestampNumber', sortByOrder: 'asc'}}
            TableBodyRowComponent={(props: HealthReportSpotSignalsTableRowProps) => (<HealthReportSpotSignalsTableRow {...props} />)}
        />
    );
}
//----------------------------------------------------------------------------------------------------------------------

type HealthReportSpotSignalsTableRowProps = {
    data: BiometricHistorySpotData;
};

function HealthReportSpotSignalsTableRow({data}: HealthReportSpotSignalsTableRowProps) {
    return (
        <StyledWrapper>
            <td className="">
                {formatApiDateShortUniversal(data.fullDate.date!)}
                {' '}
                {formatApiTimeUniversal(data.fullDate.time!)}
            </td>
            <DataCell data={data} signalType={SignalType.Signal_PR} />
            <DataCell data={data} signalType={SignalType.Signal_RR} />
            <DataCell data={data} signalType={SignalType.Signal_SPO2} />
            <DataCellDual data={data} signalType1={SignalType.Signal_BP_SYS} signalType2={SignalType.Signal_BP_DIA} />
            <DataCell data={data} signalType={SignalType.NEWS2} />
        </StyledWrapper>
    );
}

type DataCellProps = {
    data: BiometricHistorySpotData;
    signalType: SignalType;
};
function DataCell({data, signalType}: DataCellProps) {
    const value = data.values.find(v => v.signalType === signalType);
    return (
        <td className="">
            {!value || value.quality === 0 ? (
                <i>N/V</i>
            ) : (
                `${value.value} ${getSignalTypeUnit(signalType)}`
            )}
        </td>
    );
}

type DataCellDualProps = {
    data: BiometricHistorySpotData;
    signalType1: SignalType;
    signalType2: SignalType;
};
function DataCellDual({data, signalType1, signalType2}: DataCellDualProps) {
    const value1 = data.values.find(v => v.signalType === signalType1);
    const value2 = data.values.find(v => v.signalType === signalType2);
    return (
        <td className="">
            {!value1 || !value2 || value1.quality === 0 || value2.quality === 0 ? (
                <i>N/V</i>
            ) : (
                `${value1.value} ${getSignalTypeUnit(signalType1)} - ${value2.value} ${getSignalTypeUnit(signalType2)}`
            )}
        </td>
    );
}

//language=SCSS
const StyledWrapper = styled.tr`
& {
    th {
        font-size: 12px;
    }
    
    .linkCell {
        padding: 5px;
    }
    
    .eventsCell {
        width: 30%;
        min-width: 110px;
        max-width: 140px;
        border-left: 4px solid transparent;
        strong {
            font-size: 16px;
        }
    }
}
`;

export {HealthReportSpotSignalsTable};
